/**
 * ListRow — Push 81 (2026-07-23) row primitive companion to `<ListViewShell>`.
 * ============================================================================
 * Wraps a table row (`<tr>`) with:
 *   - Unified row-click drill (Push 51 interactive-descendant bail-out
 *     discipline) — the row click handler skips descendants matching
 *     `input, select, textarea, button, a, label, .kebab-wrap,
 *     [data-inline-editable], .tag-chip-editor`.
 *   - Optional bulk-select checkbox column (leftmost, rendered only when
 *     `bulkSelectId` + `onBulkSelectChange` are provided).
 *   - Optional kebab menu column (rightmost, rendered only when
 *     `kebabActions` is non-empty).
 *
 * Consumers own their `<td>` cells for every column between the optional
 * checkbox + kebab. The primitive doesn't dictate column count or column
 * shape — it only owns the row-click drill + the chevron cells.
 *
 * Table-shape only. Card-list rows (AdminLibraryList document rows) do NOT
 * use this primitive — they'd need a `<div>` shape variant that would either
 * fork logic or force card-list markup into a table-shape it doesn't want.
 * Push 82 sweep may extend to card-list surfaces if the shape lands cleanly.
 *
 * See PATTERNS.md § D "List view shape" for the full pattern context.
 */

import { type MouseEvent, type ReactNode } from 'react';

import { KebabMenu, type KebabAction } from './KebabMenu.tsx';
import { Checkbox } from './Checkbox.tsx';

// ---------------------------------------------------------------------------
// Interactive-descendant bail-out selector — matches Push 51 People.tsx +
// Push 78 SpendLimitsByUserSection + Push 79 AdminLibraryList discipline.
// Any click on a descendant matching this selector skips the row-click drill
// so nested interactive elements keep their own semantics.
// ---------------------------------------------------------------------------

const INTERACTIVE_DESCENDANT_SELECTOR =
  'input, select, textarea, button, a, label, .kebab-wrap, [data-inline-editable], .tag-chip-editor';

/**
 * Shared bail-out helper — exported for card-list surfaces (AdminLibraryList
 * AdminDocumentRow, etc.) that render `<div>` rows instead of `<tr>` rows.
 * Keeps the interactive-descendant selector in one place.
 *
 * Usage:
 *   const handleClick = (e: MouseEvent<HTMLElement>) => {
 *     if (isInteractiveDescendantClick(e)) return;
 *     navigate('...');
 *   };
 */
export function isInteractiveDescendantClick(
  event: MouseEvent<HTMLElement>,
): boolean {
  const target = event.target as HTMLElement | null;
  if (target === null) return false;
  return target.closest(INTERACTIVE_DESCENDANT_SELECTOR) !== null;
}

// ---------------------------------------------------------------------------
// ListRow props
// ---------------------------------------------------------------------------

export interface ListRowProps {
  /**
   * Row-click drill handler. Fires only when the click target is NOT an
   * interactive descendant. Consumer typically calls `navigate('...')` in
   * the handler. Omit to render a static row without drill semantics.
   */
  onRowClick?: (event: MouseEvent<HTMLTableRowElement>) => void;
  /**
   * Kebab menu actions — when non-empty, primitive renders a rightmost
   * `<td>` column with a `<KebabMenu>` trigger.
   */
  kebabActions?: KebabAction[];
  /**
   * aria-label for the kebab trigger button (matches People.tsx pattern
   * "Row actions for {name}").
   */
  kebabTriggerLabel?: string;
  /**
   * When provided, primitive renders a leftmost `<td>` column with a
   * `<Checkbox>`. Consumer's `<thead>` should match with a matching leftmost
   * `<th>` (typically carrying the select-all header checkbox).
   */
  bulkSelectId?: string;
  /** Whether this row's checkbox is currently checked. */
  bulkSelected?: boolean;
  /** Fires on checkbox change. */
  onBulkSelectChange?: (next: boolean) => void;
  /** Grays out the checkbox (matches People.tsx isMe / bulkBusy discipline). */
  bulkSelectDisabled?: boolean;
  /** aria-label for the row's bulk checkbox (matches Push 66 discipline). */
  bulkSelectAriaLabel?: string;
  /** Show the bulk-select cell EVEN when this row's own checkbox is hidden
   *  (rare — for a "you can't select yourself" row that must still align
   *  with sibling rows' checkbox column). Default false. */
  bulkSelectHidden?: boolean;
  /** Row-level cursor override — defaults to 'pointer' when `onRowClick`
   *  is provided; consumers can force 'default' for locked-editing states
   *  (matches People.tsx isEditingName). */
  cursorMode?: 'pointer' | 'default';
  /** Consumer's row cells — everything between the checkbox + kebab columns. */
  children: ReactNode;
  /** Optional key override (usually the row's id — consumer sets this on
   *  the `<ListRow>` element itself, not via prop). */
}

export function ListRow({
  onRowClick,
  kebabActions,
  kebabTriggerLabel,
  bulkSelectId,
  bulkSelected = false,
  onBulkSelectChange,
  bulkSelectDisabled = false,
  bulkSelectAriaLabel,
  bulkSelectHidden = false,
  cursorMode,
  children,
}: ListRowProps) {
  const hasBulkColumn =
    bulkSelectId !== undefined && onBulkSelectChange !== undefined;
  const hasKebabColumn = kebabActions !== undefined && kebabActions.length > 0;

  function handleClick(e: MouseEvent<HTMLTableRowElement>) {
    if (onRowClick === undefined) return;
    if (isInteractiveDescendantClick(e)) return;
    onRowClick(e);
  }

  const effectiveCursor =
    cursorMode ?? (onRowClick !== undefined ? 'pointer' : 'default');

  return (
    <tr
      onClick={onRowClick !== undefined ? handleClick : undefined}
      style={{ cursor: effectiveCursor }}
    >
      {hasBulkColumn && (
        <td style={{ width: 32 }}>
          {!bulkSelectHidden && (
            <Checkbox
              {...(bulkSelectAriaLabel !== undefined
                ? { 'aria-label': bulkSelectAriaLabel }
                : {})}
              checked={bulkSelected}
              onChange={onBulkSelectChange!}
              disabled={bulkSelectDisabled}
            />
          )}
        </td>
      )}
      {children}
      {hasKebabColumn && (
        <td className="col-right" style={{ width: 40 }}>
          <KebabMenu
            actions={kebabActions!}
            {...(kebabTriggerLabel !== undefined
              ? { triggerLabel: kebabTriggerLabel }
              : {})}
          />
        </td>
      )}
    </tr>
  );
}
